import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import { ROUTES } from "@/utils/enums";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/login",
    name: ROUTES.LOGIN,
    component: () => import("@/views/Login"),
  },
  {
    path: "/login/email",
    name: ROUTES.LOGIN_EMAIL,
    component: () => import("@/views/Login/LoginEmail.vue"),
  },
  {
    path: "/end",
    name: ROUTES.END,
    component: () => import("@/views/End"),
  },
  {
    path: "/agentic/approval",
    alias: "/approval",
    name: ROUTES.AGENTIC_APPROVAL,
    component: () => import("@/views/Agentic/ApprovalScreen"),
  },
  {
    path: "/profile/create",
    name: ROUTES.CREATE_PROFILE,
    component: () => import("@/views/CreateProfile"),
  },
  {
    path: "/profile/update",
    name: ROUTES.UPDATE_PROFILE,
    component: () => import("@/views/UpdateProfile"),
  },
  {
    path: "/create-organization",
    name: ROUTES.CREATE_ORGANIZATION,
    component: () => import("@/views/CreateOrganization"),
  },
  {
    path: "/invitation",
    name: ROUTES.INVITATION,
    component: () => import("@/views/Invitation"),
  },
  {
    path: "/accept-invite",
    name: ROUTES.ACCEPT_INVITE,
    component: () => import("@/views/AcceptInvite"),
  },
  {
    // match both /organization and /organization/:teamName because we handle
    // redirecting to the correct team and route in the Home component
    path: "/organization/:teamName?",
    alias: "/organization",
    name: ROUTES.HOME,
    component: () => import("@/views/Home"),
    children: [
      {
        path: "profile-settings",
        name: ROUTES.PROFILE_SETTINGS,
        component: () => import("@/containers/ProfileSettings"),
      },
      {
        path: "getting-started",
        name: ROUTES.GETTING_STARTED,
        component: () => import("@/containers/GettingStarted"),
        props: true,
      },
      {
        path: "projects",
        name: ROUTES.TEAM_PROJECTS,
        component: () => import("@/containers/Projects"),
        props: true,
      },
      {
        path: "archive",
        name: ROUTES.TEAM_ARCHIVE,
        component: () => import("@/containers/Archive"),
        props: true,
      },
      {
        path: "verifiers",
        name: ROUTES.TEAM_VERIFIERS,
        component: () => import("@/containers/Verifiers"),
        props: true,
      },
      {
        path: "verifiers/:verifierId",
        name: ROUTES.TEAM_VERIFIER_DETAILS,
        component: () => import("@/containers/Verifiers/Verifier"),
        props: true,
      },
      {
        path: "settings",
        name: ROUTES.TEAM_SETTINGS,
        component: () => import("@/containers/Settings"),
        props: true,
        redirect: { name: ROUTES.TEAM_SETTINGS_GENERAL },
        children: [
          {
            path: "general",
            name: ROUTES.TEAM_SETTINGS_GENERAL,
            component: () => import("@/containers/Settings/Overview/Overview.vue"),
          },
          {
            path: "members",
            name: ROUTES.TEAM_SETTINGS_MEMBERS,
            component: () => import("@/containers/Team/MemberList"),
          },
        ],
      },
      {
        path: "billing",
        name: ROUTES.TEAM_BILLING,
        component: () => import("@/containers/Billing"),
        props: true,
        redirect: { name: ROUTES.TEAM_BILLING_PLAN_AND_SUBSCRIPTION },
        children: [
          {
            path: "plan-and-subscription",
            name: ROUTES.TEAM_BILLING_PLAN_AND_SUBSCRIPTION,
            component: () => import("@/components/Billing/PlanAndSubscription"),
          },
          {
            path: "payment",
            name: ROUTES.TEAM_BILLING_PAYMENT,
            component: () => import("@/components/Billing/Payment"),
          },
        ],
      },
      {
        path: "projects/:projectId",
        name: ROUTES.TEAM_PROJECT,
        component: () => import("@/containers/Project"),
        props: true,
        children: [
          {
            path: "",
            name: ROUTES.TEAM_PROJECT_SETTINGS,
            component: () => import("@/containers/Project/General/ProjectSettings"),
            redirect: { name: ROUTES.TEAM_PROJECT_SETTINGS_GENERAL },
            children: [
              {
                path: "general",
                name: ROUTES.TEAM_PROJECT_SETTINGS_GENERAL,
                component: () => import("@/containers/Project/General/ProjectOverview"),
              },
              {
                path: "domains",
                name: ROUTES.TEAM_PROJECT_SETTINGS_DOMAINS,
                component: () => import("@/containers/Project/General/Domains"),
              },
              {
                path: "advanced",
                name: ROUTES.TEAM_PROJECT_SETTINGS_ADVANCED,
                component: () => import("@/containers/Project/General/Advanced"),
              },
            ],
          },
          {
            path: "analytics",
            name: ROUTES.TEAM_PROJECT_ANALYTICS,
            component: () => import("@/containers/Project/General/Analytics"),
          },
          {
            path: "access-control",
            name: ROUTES.TEAM_PROJECT_ACCESS_CONTROL,
            component: () => import("@/containers/Project/General/AccessControl"),
          },
          {
            path: "chain-networks",
            name: ROUTES.TEAM_PROJECT_CHAIN_NETWORKS,
            component: () => import("@/containers/Project/Authentication/ChainNetworks"),
          },
          {
            path: "authentication",
            name: ROUTES.TEAM_PROJECT_AUTHENTICATION,
            component: () => import("@/containers/Project/Authentication/Authentication"),
            redirect: { name: ROUTES.TEAM_PROJECT_SOCIAL_CONNECTIONS },
            children: [
              {
                path: "social-connections",
                name: ROUTES.TEAM_PROJECT_SOCIAL_CONNECTIONS,
                component: () => import("@/containers/Project/Authentication/Authentication/SocialConnections.vue"),
              },
              {
                path: "custom-connections",
                name: ROUTES.TEAM_PROJECT_CUSTOM_CONNECTIONS,
                component: () => import("@/containers/Project/Authentication/Authentication/CustomConnections.vue"),
              },
              {
                path: "group-connections",
                name: ROUTES.TEAM_PROJECT_GROUP_CONNECTIONS,
                component: () => import("@/containers/Project/Authentication/Authentication/GroupConnection/GroupedConnections.vue"),
              },
              {
                path: "archived-connections",
                name: ROUTES.TEAM_PROJECT_ARCHIVED_CONNECTIONS,
                component: () => import("@/containers/Project/Authentication/Authentication/ArchivedConnections/ArchivedConnections.vue"),
              },
            ],
          },
          {
            path: "wallet-configuration",
            name: ROUTES.TEAM_PROJECT_WALLET_CONFIGURATION,
            component: () => import("@/containers/Project/WalletServices/WalletConfiguration"),
          },
          {
            path: "wallet-funding",
            name: ROUTES.TEAM_PROJECT_WALLET_FUNDING,
            component: () => import("@/containers/Project/WalletServices/WalletFunding"),
          },
          {
            path: "smart-accounts",
            name: ROUTES.TEAM_PROJECT_SMART_ACCOUNTS,
            component: () => import("@/containers/Project/Authentication/SmartAccounts"),
            redirect: { name: ROUTES.TEAM_PROJECT_SMART_ACCOUNTS_GENERAL },
            children: [
              {
                path: "general",
                name: ROUTES.TEAM_PROJECT_SMART_ACCOUNTS_GENERAL,
                component: () => import("@/containers/Project/Authentication/SmartAccounts/General.vue"),
              },
              {
                path: "chains-networks",
                name: ROUTES.TEAM_PROJECT_SMART_ACCOUNTS_CHAINS_NETWORKS,
                component: () => import("@/containers/Project/Authentication/SmartAccounts/SmartAccountChainsAndNetworks.vue"),
              },
            ],
          },
          {
            path: "branding",
            name: ROUTES.TEAM_PROJECT_BRANDING,
            component: () => import("@/containers/Project/Customization/Branding"),
          },
          {
            path: "login-modal",
            name: ROUTES.TEAM_PROJECT_LOGIN_MODAL,
            component: () => import("@/containers/Project/Customization/LoginModal"),
          },
          {
            path: "users",
            name: ROUTES.TEAM_PROJECT_USERS,
            component: () => import("@/containers/Project/UserManagement/Users"),
          },
        ],
      },
    ],
  },
  {
    path: "/agentic-test",
    name: ROUTES.AGENTIC_TEST,
    component: () => import("@/views/AgenticTest"),
  },
  {
    path: "/agentic",
    name: ROUTES.AGENTIC_ENTRY,
    component: () => import("@/views/Agentic/AgenticApp.vue"),
    children: [
      {
        path: "login",
        name: ROUTES.AGENTIC_LOGIN,
        component: () => import("@/views/Agentic/AgenticLogin.vue"),
      },
      {
        path: ":teamName",
        name: ROUTES.AGENTIC_ORGANIZATION,
        component: () => import("@/views/Agentic/AgenticOrganization.vue"),
        children: [
          {
            path: "projects",
            name: ROUTES.AGENTIC_PROJECTS,
            component: () => import("@/views/Agentic/AgenticProjects.vue"),
          },
          {
            path: "projects/:projectId",
            name: ROUTES.AGENTIC_PROJECT_DETAILS,
            component: () => import("@/views/Agentic/AgenticProjectDetail.vue"),
            props: true,
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

export default router;
